import { Link } from "@react-navigation/native";
import React from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import {
    StyleFunction,
    useThemedStyle,
    useTheme,
    CheckBox,
    Text,
    IconButton,
    ColorIndicator,
} from "@venuepos/react-common";
import { Icon } from "@venuepos/react-common";
import { DataTable } from "@venuepos/react-common";
import { formatQuantity } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import { sharedStyleFunc } from "./product-list-screen";
import { GQProductsQuery } from "graphql-sdk";

export const ProductRow = React.memo(
    (props: {
        item: GQProductsQuery["products"]["data"][0];
        formattedAmount: string;
        selectedProduct: boolean;
        onSelect: (id: string) => void;
        onMultiSelectChange: (selected: boolean, ids: string[]) => void;
        productId: string;
        style?: StyleProp<ViewStyle>;
        locale: AvailableLocale;
    }) => {
        const [t] = useTranslation();
        const styles = useThemedStyle(styleFunc);
        const sharedStyles = useThemedStyle(sharedStyleFunc);
        const theme = useTheme();
        const {
            item,
            formattedAmount,
            onSelect,
            selectedProduct,
            onMultiSelectChange,
            productId,
        } = props;

        return (
            <DataTable.Row style={props.style}>
                <DataTable.Cell style={sharedStyles.iconColumn}>
                    <CheckBox
                        value={selectedProduct}
                        onValueChange={value =>
                            onMultiSelectChange(value, [productId])
                        }
                        style={styles.checkbox}
                        testID={"select:" + item.name}
                    />
                </DataTable.Cell>
                <DataTable.Cell style={sharedStyles.activeColumn}>
                    <ColorIndicator
                        status={item.active ? "success" : "warning"}
                        animate={false}
                    />
                </DataTable.Cell>
                <DataTable.Cell style={sharedStyles.productNameColumn}>
                    {item.parent ? (
                        <View>
                            <Text style={styles.parentName}>
                                {item.parent.name}
                                <Icon
                                    name="arrow-down"
                                    size="tiny"
                                    color={theme.colors.grey500}
                                    style={styles.arrowIcon}
                                />
                            </Text>
                            <Link
                                numberOfLines={1}
                                to={{
                                    screen: "PRODUCT_EDIT",
                                    params: { id: item.id },
                                }}
                                style={theme.styles.link}
                            >
                                {item.name}
                            </Link>
                        </View>
                    ) : (
                        <Link
                            numberOfLines={1}
                            to={{
                                screen: "PRODUCT_EDIT",
                                params: { id: item.id },
                            }}
                            style={theme.styles.link}
                        >
                            {item.name}
                        </Link>
                    )}
                </DataTable.Cell>
                <DataTable.Cell>{item.externalId}</DataTable.Cell>
                <DataTable.Cell>
                    <Link
                        to={{
                            screen: "PRODUCT_GROUP_EDIT",
                            params: { id: item.group.id },
                        }}
                        style={theme.styles.link}
                    >
                        {item.group.name}
                    </Link>
                </DataTable.Cell>
                <DataTable.Cell style={sharedStyles.externalColumn}>
                    {item.isExternal ? t("common.yes", "Yes") : null}
                </DataTable.Cell>
                <DataTable.Cell numeric>{item.plu}</DataTable.Cell>
                <DataTable.Cell numeric>{formattedAmount}</DataTable.Cell>
                <DataTable.Cell
                    numeric
                    style={sharedStyles.vatColumn}
                >{`${item.vat.percentage}%`}</DataTable.Cell>
                <DataTable.Cell
                    numeric
                    style={sharedStyles.discountTemplateColumn}
                >
                    {item.discountTemplate?.length || ""}
                </DataTable.Cell>
                <DataTable.Cell numeric style={sharedStyles.quantityColumn}>
                    {formatQuantity(item.inventoryProduct?.quantity || 0, {
                        locale: props.locale,
                        maximumDecimals: 3,
                    })}
                </DataTable.Cell>
                <DataTable.Cell numeric style={sharedStyles.iconColumn}>
                    <IconButton
                        color={theme.colors.secondary}
                        name="edit"
                        onPress={() => onSelect(productId)}
                        testID={"product:edit:" + item.name}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        );
    }
);

const styleFunc: StyleFunction = theme => ({
    parentName: {
        fontSize: 10,
        color: theme.colors.grey800,
    },
    arrowIcon: {
        paddingLeft: theme.spacingScale / 2,
    },
});
